import React, { useState } from "react";
import { Input, Card, Switch, Select, DatePicker, InputNumber, theme, Button } from "antd";
import dayjs from "dayjs";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  SoundTwoTone,
} from "@ant-design/icons";
import FileUpload from "./FileUpload";
import TimePickerMobile from "./TimePickerMobile";
import "../App.css";
import { Question } from "../types";
import { useAppTheme } from "../theme/ThemeProvider";
import {
  getAnswerValueCandidates,
  normalizeAnswerCompareValue,
  parseIncidentTaskValues,
  parseIncidentValues,
  shouldAutoEnableAlarm,
} from "../utils/answerIncident";

const { Option } = Select;

interface Answer {
  [key: string]: unknown;
}

interface QuestionGroupProps {
  questions: Question[];
  answers: Record<string, Answer>;
  onChange: (questionId: string, field: string, value: unknown) => void;
}

export default function QuestionGroup({
  questions,
  answers,
  onChange,
}: QuestionGroupProps) {
  const { token } = theme.useToken();
  const { themeName } = useAppTheme();
  const isGuest = !(localStorage.getItem("userId") || "").trim();
  const [timePickers, setTimePickers] = useState<{ [key: string]: { visible?: boolean; value?: { hour: string; minute: string } } }>({});
  const getQuestionStateKey = (question: Question): string =>
    `${question.inventory_id || ""}::${question.id}`;
  const onQuestionChange = (
    question: Question,
    field: string,
    value: unknown
  ) => onChange(getQuestionStateKey(question), field, value);

  const renderAlarm = (question: Question, answer: Answer) => {
    if (!question.attach_alert || answer.skip_check) return <span style={{ marginRight: 10, marginLeft: 10 }}></span>;
    return (
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <SoundTwoTone
          twoToneColor={
            themeName === "dark"
              ? ["#3ee7ed", "#0d1117"]
              : ["#000000", "#000000"]
          }
          style={{ marginRight: 10, fontSize: 23 }}
        />
        <Switch
          checked={
            Boolean(answer?.is_alert) ||
            (!isGuest && shouldAutoEnableAlarm(question, answer))
          }
          onChange={(checked) => onQuestionChange(question, "is_alert", checked)}
        />
      </span>
    );
  };

  const renderSkipCheck = (question: Question, answer: Answer) => {
    if (question.skip_check) {
      return (
        <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Switch
            checked={Boolean(answer?.skip_check)}
            onChange={(checked) => onQuestionChange(question, "skip_check", checked)}
          />
        </span>
      );
    }
    return <span style={{ marginRight: 10, marginLeft: 10 }}></span>;
  };

  const renderAttachTask = (question: Question, answer: Answer) => {
    if (isGuest) return <span style={{ marginRight: 10, marginLeft: 10 }}></span>;
    if (!question.attach_task) return <span style={{ marginRight: 10, marginLeft: 10 }}></span>;
    const completed = Boolean((answer as any).task_completed);
    return (
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {completed ? (
          <CheckCircleTwoTone
            twoToneColor={["#39d353", "#0d1117"]}
            style={{ marginRight: 10, fontSize: 23 }}
          />
        ) : (
          <CloseCircleTwoTone
            twoToneColor={["#f85149", "#0d1117"]}
            style={{ marginRight: 10, fontSize: 23 }}
          />
        )}
        <Switch
          checked={completed}
          onChange={(checked) => onQuestionChange(question, "task_completed", checked)}
          checkedChildren="completed"
          unCheckedChildren="in progress"
        />
      </span>
    );
  };

  const renderMandatoryWarning = (condition: boolean) =>
    condition ? (
      <span style={{ color: token.colorError }}>Toto pole je povinné.</span>
    ) : null;

  const normalizeDigitsOnly = (raw: string): string => raw.replace(/[^\d]/g, "");

  const hasAnyAnswer = (question: Question, answer: Answer): boolean => {
    if (question.type === "boolean") return answer.val_boolean != null;
    return (
      answer.val_text != null ||
      answer.val_number != null ||
      answer.val_real != null ||
      answer.val_lov != null ||
      answer.val_file != null
    );
  };

  const parseLovOptions = (question: Question) => {
    let options: string[] = [];
    if (question.options && question.options.length > 0) {
      options = question.options;
    } else if (question.lov_values) {
      options = question.lov_values.split("|");
    }
    options = options.concat("Jiný důvod");
    return options.map((opt) => (
      <Option
        style={{ width: "100%", height: 50, fontSize: 17 }}
        key={opt}
        value={opt}
      >
        {opt}
      </Option>
    ));
  };

  const renderAttachNote = (question: Question, answer: Answer) => {
    if (question.type === "text") return null;
    if (question.attach_note) {
      return (
        <div style={{ marginTop: 12 }}>
          <label style={{ fontWeight: 500}}>Popis</label>
          <Input.TextArea
            rows={2}
            placeholder="Zadejte popis"
            value={String(answer.note ?? "")}
            onChange={(e) => onQuestionChange(question, "note", e.target.value)}
          />
        </div>
      );
    }

    if (answer.val_lov !== "Jiný důvod") return null;

    const hasPrimaryValue =
      (question.type === "lov" && answer.val_lov) ||
      (question.type === "lov" && answer.val_lov === "Jiný důvod") ||
      (question.type === "number" && answer.val_number != null) ||
      (question.type === "real" && answer.val_real != null) ||
      (question.type === "date" && answer.val_text !== null) ||
      (question.type === "datetime" && answer.val_text !== null) ||
      (question.type === "boolean" && answer.val_boolean === false);

    if (!hasPrimaryValue) return null;

    return (
      <div style={{ marginTop: 12 }}>
        <label style={{ fontWeight: 500, color: themeName === "dark" ? "#3ee7ed" : undefined }}>Popis</label>
        <Input.TextArea
          rows={2}
          placeholder="Zadejte popis"
          value={String(answer.note ?? "")}
          onChange={(e) => onQuestionChange(question, "note", e.target.value)}
        />
      </div>
    );
  };

  const renderCard = (question: Question, answer: Answer, content: React.ReactNode) => {
    const isSkip = answer.skip_check === true;
    const isBooleanFalse = question.type === "boolean" && answer?.val_boolean === false;
    const incidentValues = parseIncidentValues(question.incident);
    const answerCandidates = getAnswerValueCandidates(question, answer);
    const incidentSet = new Set(incidentValues.map(normalizeAnswerCompareValue));
    const isIncidentMatched = answerCandidates.some((c) => incidentSet.has(normalizeAnswerCompareValue(c)));
    const incidentTaskValues = parseIncidentTaskValues(question.incident);
    const incidentTaskSet = new Set(incidentTaskValues.map(normalizeAnswerCompareValue));
    const canCreateTaskFromAnswer = answerCandidates.some((c) =>
      incidentTaskSet.has(normalizeAnswerCompareValue(c))
    );
    const isTaskCompleted = Boolean((answer as any)?.task_completed);
    const answered = hasAnyAnswer(question, answer);

    const palette = {
      neutral: {
        border: themeName === "dark" ? "#21262d" : token.colorBorder,
        bg: themeName === "dark" ? "#161b22" : "#ffffff80",
      },
      success:
        themeName === "dark"
          ? { border: "#2a8f5a", bg: "rgba(57, 211, 83, 0.16)" }
          : { border: "#52c41a", bg: "#52c41a80" },
      danger:
        themeName === "dark"
          ? { border: "#f85149", bg: "rgba(248, 81, 73, 0.16)" }
          : { border: "#ff4d4f", bg: "#ff4d4f80" },
      skip: {
        border: themeName === "dark" ? "#21262d" : token.colorBorder,
        bg: themeName === "dark" ? "#161b22" : "#ffffff80",
      },
    };

    let borderColor = palette.neutral.border;
    let backgroundColor = palette.neutral.bg;
    let cardStateClass = "question-card--neutral";
    if (isSkip) {
      borderColor = palette.skip.border;
      backgroundColor = palette.skip.bg;
      cardStateClass = "question-card--skip";
    } else if (!isGuest && isIncidentMatched) {
      borderColor = palette.danger.border;
      backgroundColor = palette.danger.bg;
      cardStateClass = "question-card--danger";
    } else if (!isGuest && question.type === "boolean" && isBooleanFalse) {
      borderColor = palette.danger.border;
      backgroundColor = palette.danger.bg;
      cardStateClass = "question-card--danger";
    } else if (!isGuest && question.type !== "boolean" && answer?.is_alert) {
      borderColor = palette.danger.border;
      backgroundColor = palette.danger.bg;
      cardStateClass = "question-card--danger";
    } else if (!isGuest && answered) {
      borderColor = palette.success.border;
      backgroundColor = palette.success.bg;
      cardStateClass = "question-card--success";
    }

    const attachFileBackgroundColor =
      themeName === "light"
        ? cardStateClass === "question-card--danger"
          ? "#e0a6a6"
          : cardStateClass === "question-card--success"
            ? "#d1ecc9"
            : backgroundColor
        : backgroundColor;

    const iconBool =
      !isSkip &&
      question.type === "boolean" &&
      (isBooleanFalse ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "16px",
          }}
        >
          <CloseCircleTwoTone
            onClick={() => onQuestionChange(question, "val_boolean", true)}
            twoToneColor="#ff4d4f"
            style={{ fontSize: 48 }}
          />
        </div>
      ) : (
        <div
          style={{ display: "flex", justifyContent: "end", marginTop: "16px" }}
        >
          <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 48 }} />
        </div>
      ));

    return (
      <Card
        className={`question-card ${cardStateClass}`}
        key={getQuestionStateKey(question)}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 12,
            }}
          >
            {question.skip_check ? renderSkipCheck(question, answer) : null}
            <span
              style={{
                fontWeight: 600,
                flex: 1,
                wordBreak: "break-word",
                whiteSpace: "normal",
                display: "block",
              }}
            >
              {question.text || question.question_text}
            </span>
          </div>
        }
        style={{
          marginBottom: 16,
          backgroundColor,
          borderColor,
          opacity: isSkip ? 0.6 : 1,
        }}
        hoverable={!isSkip && question.type === "boolean"}
      >
        <div
          onClick={
            !isSkip && question.type === "boolean" && !isBooleanFalse
              ? () => onQuestionChange(question, "val_boolean", false)
              : undefined
          }
        >
          {isSkip ? (
            <>
              <div style={{ textAlign: "center", maxHeight: "112px" }}>
                <div style={{ marginBottom: 12, fontSize: 16, color: token.colorTextSecondary }}>
                  Nehodnoceno
                </div>
              </div>
            </>
          ) : (
            <>
              {content}
              {question.attach_file &&
                question.type !== "file" &&
                ((question.type === "boolean" && isBooleanFalse) ||
                  question.type !== "boolean") && (
                <div
                  style={{
                    marginTop: 16,
                    border: `2px dashed ${token.colorBorder}`,
                    padding: 16,
                    borderRadius: 8,
                    backgroundColor: attachFileBackgroundColor,
                  }}
                >
                  <FileUpload
                    questionId={getQuestionStateKey(question)}
                    value={Array.isArray(answer.val_file) ? answer.val_file : undefined}
                    onChange={onChange}
                  />
                </div>
              )}
              {!isGuest && canCreateTaskFromAnswer && (
                <div
                  style={{
                    marginTop: 12,
                    marginBottom: 8,
                    padding: 12,
                    borderRadius: 8,
                    border: `1px solid ${isTaskCompleted ? "#2a8f5a" : "#f0b429"}`,
                    background: themeName === "dark" ? "#0d1117" : "#fffbe6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 13, lineHeight: 1.35 }}>
                    {isTaskCompleted
                      ? "Tato odpověď vytvoří úkol se stavem completed."
                      : "Tato odpověď vytvoří úkol k pozdějšímu řešení."}
                  </span>
                  <Button
                    size="small"
                    type={isTaskCompleted ? "default" : "primary"}
                    onClick={() =>
                      onQuestionChange(
                        question,
                        "task_completed",
                        isTaskCompleted ? false : true
                      )
                    }
                  >
                    {isTaskCompleted ? "Zrušit" : "Já to vyřešil"}
                  </Button>
                </div>
              )}
              {iconBool}
            </>
          )}

          <div
            style={{
              marginTop: 12,
              paddingTop: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
              {renderAlarm(question, answer)}
            </div>
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
              {renderAttachTask(question, answer)}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div>
      {questions.map((question) => {
        const answer = (answers[getQuestionStateKey(question)] || {}) as Answer;
        const isSkip = answer.skip_check === true;

        if (isSkip) {
          return renderCard(question, answer, null);
        }

        switch (question.type) {
          case "boolean": {
            const isFalseSelected = answer.val_boolean === false;
            return renderCard(
              question,
              answer,
              <>
                {isFalseSelected && (
                  <>
                    {question.mandatory &&
                      !String(answer.val_text ?? "").trim() &&
                      renderMandatoryWarning(true)}
                    {question.options && question.options.length > 0 && (
                      <Select
                        style={{
                          width: "100%",
                          height: 50,
                          fontSize: 18,
                          marginBottom: "16px",
                        }}
                        value={answer.val_lov != null ? String(answer.val_lov) : undefined}
                        onChange={(val) => onQuestionChange(question, "val_lov", val)}
                        placeholder="Vyberte možnost"
                      >
                        {parseLovOptions(question)}
                      </Select>
                    )}
                    {renderAttachNote(question, answer)}
                  </>
                )}
              </>
            );
          }

          case "text":
            return renderCard(
              question,
              answer,
              <>
                {question.mandatory &&
                  !String(answer.val_text ?? "").trim() &&
                  renderMandatoryWarning(true)}
                <Input.TextArea
                  rows={3}
                  value={String(answer.val_text ?? "")}
                  onChange={(e) =>
                    onQuestionChange(question, "val_text", e.target.value)
                  }
                />
              </>
            );

          case "number":
            return renderCard(
              question,
              answer,
              <>
                <InputNumber
                  className="always-visible-number-arrows"
                  style={{ width: "100%", height: 50, fontSize: 18 }}
                  placeholder="Pouze číslice"
                  value={answer.val_number as number | null | undefined}
                  step={1}
                  precision={0}
                  controls
                  keyboard
                  parser={(raw) => {
                    const digits = normalizeDigitsOnly(String(raw ?? ""));
                    return digits === "" ? Number.NaN : Number(digits);
                  }}
                  onChange={(val) => {
                    // keep strict digits-only rule
                    const next = val == null ? null : Number(val);
                    onQuestionChange(question, "val_number", next != null && Number.isFinite(next) ? next : null);
                  }}
                />
                {question.mandatory && answer.val_number == null && renderMandatoryWarning(true)}
                {renderAttachNote(question, answer)}
              </>
            );

          case "real":
            return renderCard(
              question,
              answer,
              <>
                <InputNumber
                  className="always-visible-number-arrows"
                  style={{ width: "100%", height: 50, fontSize: 18 }}
                  placeholder="Např. 12,5"
                  value={answer.val_real as number | null | undefined}
                  step={0.1}
                  precision={1}
                  controls
                  keyboard
                  decimalSeparator=","
                  // allow typing comma, but parse to number (InputNumber internal uses dot)
                  parser={(raw) => {
                    const s = String(raw ?? "");
                    const cleaned = s.replace(/[^\d,]/g, "");
                    const normalized = cleaned.replace(",", ".");
                    return normalized === "" ? Number.NaN : Number(normalized);
                  }}
                  formatter={(val) => {
                    if (val == null) return "";
                    return String(val).replace(".", ",");
                  }}
                  onChange={(val) => {
                    const next = val == null ? null : Number(val);
                    onQuestionChange(question, "val_real", next != null && Number.isFinite(next) ? next : null);
                  }}
                />
                {question.mandatory && answer.val_real == null && renderMandatoryWarning(true)}
                {renderAttachNote(question, answer)}
              </>
            );

          case "lov":
            return renderCard(
              question,
              answer,
              <>
                {question.mandatory &&
                  !String(answer.val_lov ?? "").trim() &&
                  renderMandatoryWarning(true)}
                <Select
                  style={{
                    width: "100%",
                    height: 50,
                    fontSize: 18,
                    marginBottom: "16px",
                  }}
                  value={answer.val_lov != null ? String(answer.val_lov) : undefined}
                  onChange={(val) => onQuestionChange(question, "val_lov", val)}
                  placeholder="Vyberte možnost"
                >
                  {parseLovOptions(question)}
                </Select>
                {renderAttachNote(question, answer)}
              </>
            );

          case "file":
            return (
              <>
                {renderCard(
                  question,
                  answer,
                  <FileUpload
                    questionId={getQuestionStateKey(question)}
                    value={Array.isArray(answer.val_file) ? answer.val_file : undefined}
                    onChange={onChange}
                  />
                )}
                {renderAttachNote(question, answer)}
              </>
            );

          case "date":
            return (
              <>
                {renderCard(
                  question,
                  answer,
                  <>
                    <DatePicker
                      format="DD.MM.YYYY"
                      style={{
                        width: "100%",
                        height: 50,
                        fontSize: 18,
                        marginBottom: "16px",
                      }}
                      value={
                        answer.val_text != null && answer.val_text !== ""
                          ? dayjs(answer.val_text as string | number | Date)
                          : null
                      }
                      onChange={(date) =>
                        onQuestionChange(question, "val_text", date)
                      }
                    />
                    {renderAttachNote(question, answer)}
                  </>
                )}
              </>
            );

          case "datetime": {
            const questionStateKey = getQuestionStateKey(question);
            const currentVal = answer.val_text
              ? new Date(String(answer.val_text))
              : null;
            const datePart = currentVal
              ? new Date(
                  currentVal.getFullYear(),
                  currentVal.getMonth(),
                  currentVal.getDate()
                )
              : null;

            const fallbackTime = {
              hour: currentVal
                ? String(currentVal.getHours()).padStart(2, "0")
                : "00",
              minute: currentVal
                ? String(currentVal.getMinutes()).padStart(2, "0")
                : "00",
            };

            const pickerState = {
              visible: timePickers[questionStateKey]?.visible ?? false,
              value: {
                hour:
                  timePickers[questionStateKey]?.value?.hour ?? fallbackTime.hour,
                minute:
                  timePickers[questionStateKey]?.value?.minute ?? fallbackTime.minute,
              },
            };

            const updatePicker = (
              id: string,
              update: {
                visible?: boolean;
                value?: { hour: string; minute: string };
              }
            ) => {
              setTimePickers((prev) => ({
                ...prev,
                [id]: { ...prev[id], ...update },
              }));
            };

            return (
              <>
                {renderCard(
                  question,
                  answer,
                  <>
                    <DatePicker
                      format="DD.MM.YYYY"
                      style={{
                        width: "100%",
                        height: 50,
                        fontSize: 18,
                        marginBottom: 8,
                      }}
                      value={datePart ? dayjs(datePart) : null}
                      onChange={(dateMoment) => {
                        if (!dateMoment) {
                          onQuestionChange(question, "val_text", null);
                          return;
                        }

                        const prevTime =
                          timePickers[questionStateKey]?.value ?? fallbackTime;
                        const newDate = new Date(
                          dateMoment.year(),
                          dateMoment.month(),
                          dateMoment.date(),
                          Number(prevTime.hour),
                          Number(prevTime.minute),
                          0,
                          0
                        );

                        onQuestionChange(question, "val_text", newDate);
                      }}
                    />
                    <TimePickerMobile
                      value={pickerState.value}
                      disabled={isSkip}
                      onPickNow={() => {
                        const now = new Date();
                        updatePicker(questionStateKey, {
                          value: {
                            hour: String(now.getHours()).padStart(2, "0"),
                            minute: String(now.getMinutes()).padStart(2, "0"),
                          },
                        });
                        onQuestionChange(question, "val_text", now);
                      }}
                      onChange={(val) => {
                        updatePicker(questionStateKey, { value: val });

                        const baseDate = answer.val_text
                          ? new Date(String(answer.val_text))
                          : new Date();

                        const newDate = new Date(
                          baseDate.getFullYear(),
                          baseDate.getMonth(),
                          baseDate.getDate(),
                          Number(val.hour),
                          Number(val.minute),
                          0,
                          0
                        );

                        onQuestionChange(question, "val_text", newDate);
                      }}
                    />
                    {renderAttachNote(question, answer)}
                  </>
                )}
              </>
            );
          }

          default:
            return null;
        }
      })}
    </div>
  );
}
